import { useEffect, useId, useState } from 'react'

import { FxPanel } from '@/components/fx'
import { formatThreshold, MAX_THRESHOLD, MIN_THRESHOLD } from '@/pages/einstellungen/defaultThreshold'
import { SaveHint } from './SaveHint'
import { useTemplateSetting } from './useTemplateSetting'

/**
 * "Auswertung" — the confidence below which a field is sent to review.
 *
 * **This one is stored on the server**, unlike the account-wide default in
 * `/einstellungen/auswertung`, which is device-local because the schema has
 * no place for it. `form_documents.confidence_threshold` is a real column
 * and `PATCH /v1/templates/:id {confidenceThreshold}` writes it
 * (`internal/templates/postgres.go`), so the number travels with the form
 * and every reviewer sees the same one. The copy says which of the two this
 * is, because the two controls otherwise look identical.
 *
 * A native `<input type="range">`: keyboard-operable, announces its value,
 * and honours the platform's pointer behaviour — none of which a div with a
 * drag handler gets for free. The percentage beside it is the same value
 * again for anyone who cannot judge a handle's position.
 *
 * Saved on release (`onChange` moves local state, `onPointerUp`/`onKeyUp`
 * commit), so dragging from 80 to 95 is one PATCH and not fifteen.
 */
export function ThresholdSection({
  stored,
  canWrite,
}: {
  stored: number
  canWrite: boolean
}) {
  const sliderId = useId()
  const save = useTemplateSetting()
  const [draft, setDraft] = useState(stored)

  // The template is re-read after every save and on a tab switch. Track the
  // stored value unless the person is mid-drag — which is exactly what
  // `save.isPending` and a pointer still down would otherwise fight over.
  useEffect(() => {
    setDraft(stored)
  }, [stored])

  const commit = () => {
    if (draft === stored) return
    save.mutate({ confidenceThreshold: draft })
  }

  return (
    <FxPanel title="Auswertung" className="p-5 xs:p-8 sm:p-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex-1">
          <label htmlFor={sliderId} className="text-body-strong text-text">
            Schwellwert für die Prüfung
          </label>
          <p className="mt-1 text-small text-text-muted">
            Felder mit geringerer Sicherheit werden als unklar markiert. Der Wert gilt für dieses
            Musterformular und für alle, die daran arbeiten.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <input
            id={sliderId}
            type="range"
            className="h-control w-full accent-primary-strong sm:w-[220px]"
            min={Math.round(MIN_THRESHOLD * 100)}
            max={Math.round(MAX_THRESHOLD * 100)}
            step={5}
            value={Math.round(draft * 100)}
            disabled={!canWrite || save.isPending}
            // The slider works in whole percent; the stored value is the
            // 0..1 fraction the gateway keeps on the template.
            onChange={(event) => setDraft(Number(event.target.value) / 100)}
            onPointerUp={commit}
            onKeyUp={commit}
            onBlur={commit}
          />
          <output htmlFor={sliderId} className="w-16 text-right text-body-strong text-text">
            {formatThreshold(draft)}
          </output>
        </div>
      </div>
      {canWrite ? (
        <SaveHint mutation={save} saved="Schwellwert gespeichert" className="mt-3" />
      ) : (
        <p className="mt-3 text-small text-text-muted">
          Sie haben für dieses Musterformular nur Leserechte.
        </p>
      )}
    </FxPanel>
  )
}
